
import React, { useEffect, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { addDays, format } from 'date-fns'
import { useDispatch } from 'react-redux'

import useTranslation from '@/Hooks/UseTranslation'
import { useProgrammation } from '@/Hooks/UseProgrammation'
import { useProject } from '@/Hooks/UseProject'
import { useDatePicker } from '@/Hooks/UseDatePicker'

import { updateTaskAction } from '@/Redux/Actions/ProgrammationManagerActions'
import { updateTaskScheduledDates } from '@/Services/ProgrammationManagerService'

import InputText from '../../components/InputText'

const parseLocalDate = (dateStr: string) => {
  if (!dateStr) return null

  const [ y, m, dPart ] = dateStr.split('-')
  const d = dPart?.split(' ')[0]

  const year = Number(y)
  const month = Number(m)
  const day = Number(d)

  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return null
  }

  const date = new Date(year, month - 1, day)

  return isNaN(date.getTime()) ? null : date
}

const safeFormat = (dateStr: string, pattern: string) => {
  const date = parseLocalDate(dateStr)
  return date ? format(date, pattern) : ''
}

const TaskScheduledDates = () => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { itemOpened: task, patchItemOpened, setEditingItem } = useProgrammation()
  const { project, nonWorkingDays } = useProject()

  const { showDateRangePicker, hideDatePicker } = useDatePicker()

  const [ startsAt, setStartsAt ] = useState('')
  const [ endsAt, setEndsAt ] = useState('')
  const [ executeDaysReset, setExecuteDaysReset ] = useState(0)

  useEffect(() => {
    setStartsAt(safeFormat(task.startsAt, 'yyyy-MM-dd'))
    setEndsAt(safeFormat(task.endsAt, 'yyyy-MM-dd'))
  }, [task.startsAt, task.endsAt])

  const saveChanges = async (startsAt: string, endsAt: string) => {
    setEditingItem(true)

    const [ error, data ] = await updateTaskScheduledDates(task._id, startsAt, endsAt)

    setEditingItem(false)

    if (error) {
      return false
    }

    patchItemOpened({
      startsAt: data.task.startsAt,
      endsAt: data.task.endsAt,
      executeDays: data.task.executeDays,
    })

    await dispatch(updateTaskAction(data.task, data.updatedTasks))
  }

  const handleChangeDates = async (dates: any) => {

    const {
      startDate,
      endDate
    } = dates

    setStartsAt(startDate)
    setEndsAt(endDate)

    await saveChanges(startDate, endDate)

    hideDatePicker()
  }

  const handleShowDateRangePicker = () => {

    if (task.hasSubTasks) {
      return
    }

    showDateRangePicker({
      startDate: startsAt,
      endDate: endsAt,
      projectStartDate: project.startsAt,
      projectEndDate: project.endsAt,
      nonWorkingDays,
      onChangeRange: handleChangeDates
    })
  }

  const handleChangeExecuteDays = async (value: string) => {

    if (task.hasSubTasks) {
      return
    }

    const newExecuteDays = parseInt(value, 10)

    if (isNaN(newExecuteDays) || newExecuteDays < 1) {
      setExecuteDaysReset(t => t + 1)
      return
    }

    const newEndsAt = format(addDays(parseLocalDate(startsAt) as Date, newExecuteDays - 1), 'yyyy-MM-dd')

    setEndsAt(newEndsAt)

    await saveChanges(startsAt, newEndsAt)
  }

  return (
    <>
      <Row>
        <Col>
          <h5 className='group-title'>
            {t('PROGRAMMATION_MANAGER:ScheduledDatesTitleLabel')}
          </h5>
        </Col>
      </Row>
      <Row>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:ScheduledStartsAtLabel')}
            </Form.Label>
            <InputText
              value={safeFormat(startsAt, 'dd/MM/yyyy')}
              placeholder="dd/mm/yyyy"
              onClick={handleShowDateRangePicker}
              asButton
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:ScheduledEndsAtLabel')}
            </Form.Label>
            <InputText
              value={safeFormat(endsAt, 'dd/MM/yyyy')}
              placeholder="dd/mm/yyyy"
              onClick={handleShowDateRangePicker}
              asButton
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:ScheduledExecuteDaysLabel')}
            </Form.Label>
            <InputText
              key={`executeDays-${executeDaysReset}`}
              value={task.executeDays}
              onChange={handleChangeExecuteDays}
              style={{
                padding: '0.375rem !important'
              }}
            />
          </Form.Group>
        </Col>
      </Row>
    </>
  )
}

export default TaskScheduledDates
